import { builder } from '@builder.io/react';
import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { audioSetFiltersChanged } from '../../../Audio/actions/AudioActions';
import {
  selectAuthPermissions,
  selectHasAnySubscription,
  selectUseSnapshotsUI,
} from '../../../auth/AuthSelectors';
import { searchOrigins } from '../../Navigation/constants';
import { SearchFilterContentTypes } from '../../Shared/enums';
import deferToNextFrame from '../../Shared/utils/deferToNextFrame';
import {
  clearSuggestionsQuery,
  isSearchAppLoaded,
  setLoadingForSearch,
  updateSearchOptionsAndRefresh,
} from '../actions/SearchActions';
import { ProminentSearchOptionsContext } from '../components/HomepageProminentSearch';
import LandingSearchHero from '../components/LandingSearchHero';
import { ConversationalSearchProvider } from '../components/navSearch/NavSearchContext';
import { SearchBarVariant } from '../components/navSearch/NavSearchElements/searchUtils';
import ProminentSearch from '../components/navSearch/ProminentSearch';
import SearchFilterOptions from '../entities/SearchFilterOptions';
import { templateTypes } from '../entities/VideoSearchFilterOptions';
import { SearchHeroBuilderData } from '../hooks/useSearchHeroData';
import {
  selectIsEnterpriseMember,
  selectSearchFilterOptions,
} from '../selectors/searchSelectors';
import {
  encodeSearchTerm,
  fetchTypeahead,
  getDecodedSearchTerm,
} from '../utils/searchUtils';
import {
  AnySearchFilterOptions,
  SelectedSearchFilterOptions,
} from './MenuContainerInterfaces';

interface ProminentSearchProps {
  showDropDown?: boolean;
  showContentToggles?: boolean;
  alignment?: 'left' | 'center';
  useNewSearchUI?: boolean;
  showConversationalSearchCta?: boolean;
  isLanding?: boolean;
  builderData?: SearchHeroBuilderData | null;
  heroDataIsLoading?: boolean;
}

const constructSearchFilterOptions = (
  contentType: SearchFilterContentTypes,
  searchTerm = '',
  searchOrigin = searchOrigins.SEARCH_BAR
): Partial<SelectedSearchFilterOptions> => {
  const contentClass =
    SearchFilterOptions.getContentClassFromContentType(contentType);

  const templateType = templateTypes
    .find(({ urlId }) => urlId === contentType)
    ?.categoryId?.toString();

  return {
    contentClass,
    contentType,
    isPagination: false,
    page: 1,
    searchOrigin,
    searchTerm,
    similarTo: null,
    searchSimilarTitle: null,
    ...(templateType && { templateType }),
  };
};

function ProminentSearchContainer({
  showDropDown = true,
  showContentToggles = true,
  alignment = 'left',
  useNewSearchUI = false,
  showConversationalSearchCta = false,
  isLanding = false,
  builderData = null,
  heroDataIsLoading = false,
}: ProminentSearchProps) {
  const dispatch = useDispatch<any>();

  const prominentSearchOptions = React.useContext(
    ProminentSearchOptionsContext
  );

  const initialSelectedSearchFilterOptions = useSelector(
    selectSearchFilterOptions
  );

  const [selectedSearchFilterOptions, setSelectedSearchFilterOptions] =
    useState<AnySearchFilterOptions>(
      initialSelectedSearchFilterOptions
        ? initialSelectedSearchFilterOptions.update(
            constructSearchFilterOptions(
              prominentSearchOptions?.[0]?.id ||
                initialSelectedSearchFilterOptions.contentType,
              initialSelectedSearchFilterOptions.searchTerm,
              initialSelectedSearchFilterOptions.searchOrigin
            )
          )
        : {}
    );

  useEffect(() => {
    if (initialSelectedSearchFilterOptions) {
      const updatedOptions = initialSelectedSearchFilterOptions.update(
        constructSearchFilterOptions(
          prominentSearchOptions?.[0]?.id ||
            initialSelectedSearchFilterOptions.contentType,
          initialSelectedSearchFilterOptions.searchTerm,
          initialSelectedSearchFilterOptions.searchOrigin
        )
      );
      setSelectedSearchFilterOptions(updatedOptions);
    }
  }, [initialSelectedSearchFilterOptions, prominentSearchOptions]);

  const userHasAnySubscription = useSelector(selectHasAnySubscription);
  const isEnterpriseMember = useSelector(selectIsEnterpriseMember);
  const { pproPluginEnabled } = useSelector(selectAuthPermissions);
  const useSnapshotsUI = useSelector(selectUseSnapshotsUI);

  const { contentType = '', searchTerm = '' } = selectedSearchFilterOptions;
  const decodedSearchTerm = getDecodedSearchTerm(searchTerm);

  const handleDropdownChange = useCallback(
    (contentType) => {
      // This fixes https://videoblocks.atlassian.net/browse/LNL-227
      if (showDropDown) return;
      const newSelectedSearchFilterOptions = selectedSearchFilterOptions.update(
        constructSearchFilterOptions(contentType, decodedSearchTerm)
      );
      setSelectedSearchFilterOptions(newSelectedSearchFilterOptions);
    },
    [
      showDropDown,
      selectedSearchFilterOptions,
      decodedSearchTerm,
      constructSearchFilterOptions,
    ]
  );

  const handleSubmit = useCallback(
    (contentType: string, searchTerm: string, searchOrigin: string) => {
      dispatch(clearSuggestionsQuery());
      dispatch(setLoadingForSearch(true));

      // Defer work that might slow down more important UI updates.
      deferToNextFrame(() => {
        dispatch(audioSetFiltersChanged(true));
      });

      deferToNextFrame(() => {
        builder.init('e7fb3d1e4da14573bd2a1edb7bfee5f1');
        builder.track('search');
      });
      deferToNextFrame(() => {
        const encodedSearchTerm = encodeSearchTerm(searchTerm);
        const newSelectedSearchFilterOptions =
          selectedSearchFilterOptions.update(
            constructSearchFilterOptions(
              contentType as SearchFilterContentTypes,
              encodedSearchTerm,
              searchOrigin
            )
          );
        dispatch(updateSearchOptionsAndRefresh(newSelectedSearchFilterOptions));
      });
    },
    [dispatch, selectedSearchFilterOptions, constructSearchFilterOptions]
  );

  function getSearchBarVariant() {
    if (useNewSearchUI) {
      return SearchBarVariant.ProminentSearch;
    }
    return SearchBarVariant.ProminentLanding;
  }

  return (
    <ConversationalSearchProvider>
      {isLanding ? (
        <LandingSearchHero
          currentContentType={contentType as SearchFilterContentTypes}
          builderData={builderData}
          isLoading={heroDataIsLoading}
        />
      ) : (
        <ProminentSearch
          onDropdownChange={handleDropdownChange}
          onSubmit={handleSubmit}
          searchTerm={decodedSearchTerm}
          userHasAnySubscription={userHasAnySubscription}
          fetchTypeahead={fetchTypeahead}
          isEnterpriseMember={isEnterpriseMember}
          pproPluginEnabled={pproPluginEnabled}
          isSearchAppLoaded={isSearchAppLoaded}
          prominentSearchOptions={prominentSearchOptions}
          // in ProminentSearch, contentTypeFromPrevious sets default contentType (`contentType` prop unused)
          contentTypeFromPrevious={contentType as SearchFilterContentTypes}
          showDropDown={showDropDown}
          showContentToggles={showContentToggles}
          useNewSearchUI={useNewSearchUI}
          useSnapshots={useSnapshotsUI}
          alignment={alignment}
          searchBarVariant={getSearchBarVariant()}
          showConversationalSearchCta={showConversationalSearchCta}
        />
      )}
    </ConversationalSearchProvider>
  );
}

export default ProminentSearchContainer;
